<template lang="html">
  <div class="mb-3 mt-4">
    <b-row>
      <b-col md="3">
        <ReloadingImage
          v-if="image()"
          :height="150"
          :width="300"
          thumbnail
          fluid
          :src="image()"
        />
        <b-img
          v-show="!image()"
          :width="300"
          :height="200"
          blank
          blank-color="#666"
          thumbnail
        />
      </b-col>
      <b-col md="6">
        <h2>
          <span v-show="show.privacySelected != 'world'" class="smaller">
            <v-icon name="lock" />
          </span>
          {{ show.title }}
        </h2>
        <div>
          <!-- Privacy state statement. -->
          <span v-if="show.privacySelected == 'world'">
            <v-icon :name="randomGlobe()" /> This show is public
          </span>
          <span v-else> This show is private</span>
          <b-link
            v-if="!assetsNotAvailable()"
            :to="{
              name: 'show-mode-highlight',
              params: { id: show.key, mode: 'settings', highlight: 'privacy' },
            }"
            class="small-edit ml-1"
          >
            Edit
          </b-link>
        </div>

        <div v-if="assetsNotAvailable()" class="mt-3">
          <b-btn
            :to="{ name: 'show', params: { id: show.key } }"
            size="sm"
            variant="secondary"
            class="mr-1"
          >
            Import images and audio
          </b-btn>
          <b-btn
            :to="{ name: 'show-import', params: { id: show.key } }"
            size="sm"
            variant="secondary"
            class="mr-1"
            :disabled="someMediaNoTimeline()"
          >
            Import existing show
          </b-btn>
        </div>
        <div v-if="assetsNotAvailable()" class="mt-2">
          <ShowConfirmActionButton
            :key="'confirm_' + show.key"
            :show-details="show"
          />
        </div>

        <div v-if="assetsAvailable()" class="mt-3">
          <b-btn
            :to="{ name: 'show', params: { id: show.key } }"
            size="md"
            variant="primary"
            class="mr-1"
          >
            Edit <v-icon name="edit" />
          </b-btn>

          <LinkButton :show="show" />
          <ShareButton :show="show" />
        </div>
        <div v-if="image()" class="mt-2">
          <b-btn
            :to="{
              name: 'show-mode',
              params: { id: show.key, mode: 'export' },
            }"
            size="sm"
            variant="outline-secondary"
            class="mr-1"
          >
            Export video <v-icon name="download" />
          </b-btn>

          <!-- Duplicate -->
          <b-button
            size="sm"
            class="mr-1"
            variant="outline-secondary"
            :disabled="showLimitMet"
            @click="duplicateShow"
          >
            Duplicate <v-icon name="copy" />
          </b-button>
          <!-- Using value -->
          <ShowConfirmActionButton
            :key="'confirm_' + show.key"
            :show-details="show"
          />
        </div>
      </b-col>
      <b-col md="2">
        <p class="small">Updated {{ show.updated_at | age_in_words }}</p>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col>
        <hr />
        <div
          v-if="
            $store.state.showList.oldestDocSnapshotShow.key == show.key &&
              !$store.state.showList.reachedLastSnapshot
          "
        >
          <b-btn
            size="md"
            :disabled="$store.state.showList.queryInProgress"
            class="mr-1"
            @click="$store.dispatch('showList/loadShowNext')"
          >
            Next 5 shows
          </b-btn>
          <b-btn
            size="md"
            variant="outline-secondary"
            :disabled="$store.state.showList.queryInProgress"
            class="mr-1"
            @click="$store.dispatch('showList/loadShowNext', 10)"
          >
            10
          </b-btn>
          <b-btn
            size="md"
            variant="outline-secondary"
            :disabled="$store.state.showList.queryInProgress"
            class="mr-1"
            @click="$store.dispatch('showList/loadShowNext', 25)"
          >
            25
          </b-btn>
          <b-spinner
            v-if="$store.state.showList.queryInProgress"
            small
            label="Loading"
            class="small mt-1 ml-1"
          />
          <div class="float-right mt-3">
            Viewing {{ $store.state.showList.orderedShows.length }} of
            {{ $store.state.user_show_total }} shows
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { formatDistanceToNow } from "date-fns";
import find from "lodash/find";
import LinkButton from "@/components/show/LinkButton";
import ReloadingImage from "@/components/ReloadingImage";
import sample from "lodash/sample";
import ShareButton from "@/components/show/ShareButton";
import ShowConfirmActionButton from "@/components/editor/ShowConfirmActionButton";
import { Show } from "@/models/showModel";
export default {
  name: "ShowsListItem",
  filters: {
    age_in_words(value) {
      return formatDistanceToNow(new Date(value)) + " ago";
    },
  },
  components: {
    LinkButton,
    ReloadingImage,
    ShareButton,
    ShowConfirmActionButton,
  },
  props: {
    index_show_obj: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    show() {
      if (this.boundShowData) {
        return this.boundShowData;
      }
      return this.index_show_obj;
    },
    readOnlyShowModel() {
      if (this.show) {
        return new Show(this.show);
      }
      return {};
    },
    link() {
      return this.readOnlyShowModel.link;
    },
    boundShowData() {
      return find(this.$store.state.editor_shows, {
        key: this.index_show_obj.key,
      });
    },
    images() {
      // All the images uploaded.
      return ((this.show || {}).data || {}).images || [];
    },
    audio() {
      return ((this.show || {}).data || {}).audio || null;
    },
    showPreviewLink() {
      if (this.show.privacyState) {
        return this.show.privacyState == "private";
      } else {
        return !this.show.published; // Leave this here, though .published is deprecated for new shows.
      }
    },
    showLimitMet() {
      return this.$store.getters.trialShowLimitReached;
    },
  },
  methods: {
    randomGlobe() {
      return sample(["globe-americas", "globe-asia", "globe-africa"]);
    },
    someMediaNoTimeline() {
      if (this.hasSlideArray()) {
        return false;
      }
      if (!("data" in this.show)) {
        return false;
      }
      if (this.images.length > 0 && this.audio) {
        return true;
      }
      return false;
    },
    hasTimeline() {
      return this.hasSlideArray();
    },
    hasNoMedia() {
      if (this.image()) {
        return false;
      }
      if (this.images.length > 0 && this.audio) {
        return false;
      }
      return true;
    },
    assetsNotAvailable() {
      if (this.image()) {
        return false;
      }
      if (!("data" in this.show)) {
        return true;
      }
      if ("assetsAvailable" in this.show.data) {
        return !this.show.data.assetsAvailable;
      }
      return true;
    },
    assetsAvailable() {
      return !this.assetsNotAvailable();
    },
    hasRequiredAssetsButNoTimeline() {
      return (
        !this.hasSlideArray &&
        this.show.data.images.length > 0 &&
        this.show.data.audio.duration
      );
    },
    hasSlideArray() {
      if (!("data" in this.show)) {
        return false;
      }
      if ("slideArray" in this.show.data) {
        return this.show.data.slideArray.length > 0;
      }
      return false;
    },
    image() {
      var firstImage;
      var show = this.readOnlyShowModel.show;
      if (show.data.slideArray.length > 0) {
        firstImage = show.data.slideArray[0];
        if (firstImage.type == "video") {
          return `https://cdn3.soundslides.com/video/${firstImage.source_id}/${firstImage.file}_00001.jpg`;
        }
        return `https://cdn3.soundslides.com/images/${firstImage.source_id}/400/${firstImage.file}`;
      }
      if (show.data.images.length > 0) {
        firstImage = show.data.images[0];
        if (firstImage.type == "video") {
          return `https://cdn3.soundslides.com/video/${firstImage.source_id}/${firstImage.filename}_00001.jpg`;
        }
        return `https://cdn3.soundslides.com/images/${firstImage.source_id}/400/${firstImage.filename}`;
      }
    },
    duplicateShow() {
      // Only get data once for duplicate.
      this.$store.dispatch("showCreation/duplicateShowByKey", this.show.key);
    },
  },
};
</script>

<style lang="scss" scoped>
.smaller {
  position: relative;
  font-size: 0.72em;
  bottom: 0.08em;
  color: #495057;
}
.small-edit {
  font-size: 0.8em;
}
</style>
